import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

interface ScrollToTopProps {
  behavior?: ScrollBehavior;
  children: React.ReactNode;
}

const DEFAULT_SELECTOR = "[data-custom-scrollbar-viewport]";

const ScrollToTop = ({ children, behavior = "auto" }: ScrollToTopProps) => {
  const location = useLocation();
  const lastScrolledKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const locationKey =
      location.key ?? `${location.pathname}${location.search}${location.hash}`;

    // Prevent duplicate scrolling for the same location
    if (lastScrolledKeyRef.current === locationKey) {
      return;
    }
    lastScrolledKeyRef.current = locationKey;

    const target =
      (document.querySelector<HTMLElement>(
        DEFAULT_SELECTOR
      ) as HTMLElement | null) ??
      (typeof window !== "undefined" ? window : null);

    if (!target) {
      return;
    }

    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
        return;
      }
    }

    if ("scrollTo" in target) {
      target.scrollTo({ top: 0, behavior });
      return;
    }

    (target as Window).scrollTo({ top: 0, behavior });
  }, [behavior, location]);

  return <>{children}</>;
};

export default ScrollToTop;
